import { setupModeColors } from "../../SetupMode/colors";
import React from "react";
import * as io from "io-ts";
import cn from "classnames";
import SpeechBalloon from "@ui/Picto/SpeechBalloon";
import type { Message } from "./MessageCard";
import MessageCard from "./MessageCard";
import t from "../../../i18n";
import { color } from "@ui";
import createGlobalState from "@ui/lib/createGlobalState";
import { matches, hasMatches, tokenize } from "@ui/lib/hooks/useFilteredDatas";
import useDebounce from "@ui/lib/hooks/useDebounce";
import classNames from "classnames";
import Thread from "./Thread";
import type { Comment, CommentType } from "../../../api/useComments";
import {
  analysisDate,
  analysisDateIncludedInTitleShort,
  analysisTitleShort,
} from "../../Project/Analysis/analysisTitle";
import useHoveredAnalysis from "../../Project/Analysis/useHoveredAnalysis";
import useActiveAnalysesStream from "../../../api/useActiveAnalysesStream";
import type { Project } from "../../../api/useProjects";
import { useRevalidate } from "../../../api/useQuery";
import useProjectSetupMode from "../../SetupMode/useProjectSetupMode";
import { useDelete, usePatch } from "../../../api/useCall";
import type { User } from "../../../api/useUser";
import { useCanUserDoOnProject } from "../../canUserDo";
import Initial from "@ui/Initial";
import { comparatorBy, stringComparator } from "diagnostics-lib/comparator";
import { normalize } from "diagnostics-lib/string";
import BlankStateV2 from "@ui/BlankStateV2";
import { css } from "@linaria/core";
import { badgeTextOverflow } from "@ui/Badge/_styles/BadgeTextOverflow";
import { colors, fonts, spacings } from "@ui/Theme";
import formatDate from "@ui/i18n/formatDate";
import Text from "@ui/Text";
import { useFaultEventAccess } from "../../Project/Events/hooks/useFaultEventAccess";
import { Tag, tagList } from "@ui/Tag";
import { AedLine, FlagLine, Settings3Line } from "@ui/IconV2/react";
import { getEventStatusText } from "../../Project/Events/utils/faultEventStatus";
import Ellipsis from "@ui/Ellipsis";
import type { Event } from "../../../api/useEvents";

export const MAX_COMMENT_LENGTH = 4000;

const COMMENT_TAG_DELETED = "tag-deleted";
const COMMENT_TAG_CARD_HOVERED = "tag-card-hovered";
const COMMENT_TAG_CARD_SELECTED = "tag-card-selected";

interface P extends React.HTMLAttributes<HTMLDivElement> {
  project: Project;
  users: string[];
  eventIds: string[];
  eventById: Map<string, Event>;
  comments: Comment[];
  user: User;
  onPublish: (id: string, content: string) => void | Promise<void>;
  checkIfInRange: (date: number) => boolean;
  setAnalysis: (date: number | null) => void;
  search: string;
  timezone: string;
  toolBarOptions?: React.ReactNode;
}

const OUTDATED_CN = "oudated";

interface Thread extends Comment {
  answers: Comment[];
  expand: boolean;
}

interface ExpandableMessage extends Message {
  expand: boolean;
}

const useSelectedComment = createGlobalState({
  storage: "session",
  key: "selectedCommentId",
  type: io.union([io.undefined, io.string]),
  defaultValue: undefined,
  scoped: false,
});

const toSearchableData = (comment: Comment, timeZone: string) => ({
  name: comment.author.name ?? comment.author.id,
  createdBy: comment.author.id,
  analysisId: comment.analysisId === undefined ? "" : String(comment.analysisId),
  analysisTitle: comment.metadata ? analysisTitleShort(comment.metadata, timeZone) : "",
  content: comment.content,
});

const toSearchableString = (comment: Comment, timeZone: string) => {
  const data = toSearchableData(comment, timeZone);
  return normalize(
    `${data.name} ${data.createdBy} ${data.analysisId} ${data.analysisTitle} ${data.content}`,
  );
};

function ProjectCommentList(
  {
    className,
    project,
    comments,
    user,
    onPublish,
    checkIfInRange,
    setAnalysis,
    users,
    eventIds,
    eventById,
    search,
    timezone: timeZone,
    toolBarOptions,
  }: P,
  ref: React.Ref<HTMLDivElement>,
) {
  const hasFaultEventAcces = useFaultEventAccess();
  const { isSetupModeEnabledOnProject } = useProjectSetupMode(project.id);
  const revalidate = useRevalidate();
  const canEditComment = useCanUserDoOnProject("PROJECT_WRITE", project.id);
  const canEditCommentsInSetupMode = isSetupModeEnabledOnProject && canEditComment;
  const isCommentEditable = React.useCallback(
    (comment: Comment) =>
      comment.author.id === user.id &&
      (comment.type === "SETUP" ? canEditCommentsInSetupMode : true),
    [user.id, canEditCommentsInSetupMode],
  );
  const deleteComment = useDelete("/comments/:commentId(uuid)");
  const updateComment = usePatch("/comments/:commentId(uuid)");
  const [hoveredAnalysisId, setHoveredAnalysisId] = useHoveredAnalysis({});
  const [selectedCommentId, setSelectedCommentId] = useSelectedComment({});

  const { activeAnalyses } = useActiveAnalysesStream(project.id);

  const analysesIds: string[] = React.useMemo(
    () => activeAnalyses.map(analysis => analysis.id),
    [activeAnalyses],
  );

  const debouncedSearch = React.useDeferredValue(useDebounce(search, 300));
  const outdated = debouncedSearch !== search;
  const searchTokens = React.useMemo(() => tokenize(normalize(debouncedSearch)), [debouncedSearch]);

  const commentThreads: Thread[] = React.useMemo(() => {
    const initialCommentMap: { [key: string]: Thread } = comments
      .filter(comment => comment.parentId === null)
      .reduce<{ [key: string]: Thread }>((map, comment) => {
        map[comment.id] = {
          ...comment,
          answers: [],
          expand: false,
        };
        return map;
      }, {});

    for (const answer of comments) {
      if (answer.parentId !== null) {
        const parent = initialCommentMap[answer.parentId];
        // A visible RUN reply can belong to a SETUP root hidden by the current permissions/mode.
        if (!parent) continue;
        parent.answers.push(answer);
      }
    }
    return Object.values(initialCommentMap)
      .sort(comparatorBy("publishedAt", stringComparator))
      .reverse();
  }, [comments]);

  const visible = React.useCallback(
    (comment: Comment) => {
      const commentEventId = comment.faultEventId ?? comment.manualFaultEventId;

      const visible =
        hasMatches(searchTokens, toSearchableString(comment, timeZone)) &&
        (users.length < 1 || users.includes(comment.author.name ?? comment.author.id)) &&
        (eventIds.length === 0 || (commentEventId !== null && eventIds.includes(commentEventId)));

      return visible;
    },
    [searchTokens, timeZone, users, eventIds],
  );

  const filteredThreads = React.useMemo(
    () =>
      commentThreads.reduce<Thread[]>((acc, thread) => {
        if (visible(thread)) {
          acc.push(thread);
        } else if (thread.answers.some(visible)) {
          acc.push({
            ...thread,
            expand: true,
          });
        }

        return acc;
      }, []),
    [commentThreads, visible],
  );

  const threads: ExpandableMessage[] = React.useMemo(
    () =>
      filteredThreads.map(thread => {
        const answers: Message[] = thread.answers.map(answer => {
          const answerTokens = hasMatches(searchTokens, toSearchableString(answer, timeZone))
            ? searchTokens
            : [];
          const answerMatches = matches(answerTokens, toSearchableData(answer, timeZone));
          const title = (type: CommentType) => (
            <div className={titleWrapper}>
              <CommentAvatar author={answer.author} type={type} />
              <Text className={authorName} type="body1SemiBold">
                {answerMatches.name || answerMatches.createdBy}
              </Text>
              {thread.author.id === answer.author.id ? (
                <Text type="body2" className={authorTag}>
                  {t("comments.author")}
                </Text>
              ) : null}
            </div>
          );

          return {
            id: answer.id,
            type: answer.type,
            title,
            analysisTitle: null,
            analysisId: null,
            faultEvent: null,
            subtitle: null,
            isAnswer: true,
            date: answer.publishedAt,
            rawContent: answer.content,
            // content: answerMatches.content,
            answers: [],
            allowEdit: isCommentEditable(answer),
            isDeleted: false,
          };
        });

        const threadTokens = hasMatches(searchTokens, toSearchableString(thread, timeZone))
          ? searchTokens
          : [];

        const threadMatches = matches(threadTokens, toSearchableData(thread, timeZone));
        const isDeletedAnalysis =
          thread.analysisId !== null && !analysesIds.includes(thread.analysisId);

        const threadEventId = thread.faultEvent?.id ?? thread.manualFaultEventId ?? null;
        const threadEvent = threadEventId ? (eventById.get(threadEventId) ?? null) : null;

        const title = (type: CommentType) => (
          <div className={titleWrapper}>
            <CommentAvatar author={thread.author} type={type} />
            <Text type="body1SemiBold" className={authorName}>
              {threadMatches.name || threadMatches.createdBy}
            </Text>
          </div>
        );

        return {
          id: thread.id,
          type: thread.type,
          title,
          analysisId: thread.analysisId,
          faultEvent: thread.faultEvent,
          analysisTitle: threadMatches.analysisTitle,
          date: thread.publishedAt,
          rawContent: thread.content,
          content: threadMatches.content,
          answers,
          subtitle:
            isDeletedAnalysis || thread.metadata || (threadEvent && hasFaultEventAcces === true) ? (
              <div className={classNames(threadTagList, tagList)}>
                {isDeletedAnalysis ? (
                  <Tag
                    size="s"
                    className={cn(commentTag, {
                      [COMMENT_TAG_DELETED]: isDeletedAnalysis,
                    })}
                  >
                    <FlagLine size="xs" />{" "}
                    <Ellipsis>{t("project.comment.subtitle.deleted_analysis")}</Ellipsis>
                  </Tag>
                ) : (
                  <>
                    {thread.metadata ? (
                      <Tag
                        size="s"
                        title={
                          analysisDateIncludedInTitleShort(thread.metadata)
                            ? undefined
                            : analysisDate(thread.metadata.date, timeZone)
                        }
                        className={cn(badgeTextOverflow, commentTag, {
                          [COMMENT_TAG_CARD_SELECTED]:
                            checkIfInRange(thread.metadata.date) && !isDeletedAnalysis,
                          [COMMENT_TAG_CARD_HOVERED]: hoveredAnalysisId === thread.analysisId,
                        })}
                      >
                        <FlagLine size="xs" /> <Ellipsis>{threadMatches.analysisTitle}</Ellipsis>
                      </Tag>
                    ) : null}
                    {threadEvent && hasFaultEventAcces === true ? (
                      <Tag
                        className={commentTag}
                        title={getEventTitleCommentTag({
                          name: threadEvent.name,
                          startAt: formatDate(threadEvent.startsAt, "slash", timeZone),
                          closedAt: threadEvent.closedAt
                            ? formatDate(threadEvent.closedAt, "slash", timeZone)
                            : null,
                          status: threadEvent.metadata.status,
                        })}
                        size="s"
                      >
                        <AedLine size="s" /> <Ellipsis>{threadEvent.name}</Ellipsis>
                      </Tag>
                    ) : null}
                  </>
                )}
              </div>
            ) : null,
          allowEdit: isCommentEditable(thread),
          isAnswer: false,
          isDeleted: isDeletedAnalysis,
          expand: thread.expand,
        };
      }),
    [
      hasFaultEventAcces,
      analysesIds,
      checkIfInRange,
      filteredThreads,
      hoveredAnalysisId,
      searchTokens,
      timeZone,
      eventById,
      isCommentEditable,
    ],
  );

  const handleUpdate = React.useCallback(
    async ({ id, content, type }: { id: string; content: string; type?: CommentType }) => {
      await updateComment({
        body: type === undefined ? { content } : { content, type },
        routeParams: {
          commentId: id,
        },
      });
      await revalidate("/comments", {}, { projectId: project.id });
      await revalidate("/activities");
    },
    [updateComment, revalidate, project.id],
  );

  const handleDelete = React.useCallback(
    async (id: string) => {
      await deleteComment({ routeParams: { commentId: id } });
    },
    [deleteComment],
  );

  return (
    <div ref={ref} className={classNames(className, wrapper, { [OUTDATED_CN]: outdated })}>
      {threads.map(thread => (
        <Thread
          key={thread.id}
          selected={selectedCommentId === thread.id}
          setHoveredAnalysisId={setHoveredAnalysisId}
          setSelectedCommentId={setSelectedCommentId}
          setAnalysis={setAnalysis}
          analyses={activeAnalyses}
          analysisId={thread.analysisId}
          id={thread.id}
        >
          <MessageCard
            allowAction={thread.type === "SETUP" ? canEditCommentsInSetupMode : canEditComment}
            canChangeType={canEditCommentsInSetupMode}
            message={thread}
            onDelete={handleDelete}
            onEdit={handleUpdate}
            answerPlaceholder={t("comments.answer")}
            onPublish={onPublish}
            defaultDisplayAnswers={thread.expand}
            maxLength={MAX_COMMENT_LENGTH}
            toolBarOptions={toolBarOptions}
          />
        </Thread>
      ))}

      {(commentThreads.length === 0 || filteredThreads.length === 0) && (
        <BlankStateV2
          className={CommentsBlankCss}
          title={t("comments.blank_state_analysis.title")}
          subtitle={t("comments.blank_state_analysis.call_to_action")}
          picto={<SpeechBalloon />}
        />
      )}
    </div>
  );
}

export default React.forwardRef<HTMLDivElement, P>(ProjectCommentList);

const CommentsBlankCss = css`
  padding: 40px 20px;

  & > :first-child {
    height: auto;
    min-width: 0;
    padding-bottom: 8px;
  }

  & > :first-child svg {
    max-width: 220px;
    max-height: 220px;
  }
`;

function getEventTitleCommentTag({
  name,
  startAt,
  closedAt,
  status,
}: {
  name: string;
  startAt: string;
  closedAt: string | null;
  status: Parameters<typeof getEventStatusText>[0];
}) {
  return `${name}, [${startAt}${closedAt ? ` - ${closedAt}` : ""}, ${getEventStatusText(status)}]`;
}
function CommentAvatar({ author, type }: Pick<Comment, "type" | "author">) {
  if (type === "SETUP") {
    return (
      <span
        className={setupCommentAvatar}
        role="img"
        aria-label={t("comments.setup.label")}
        title={t("comments.setup.label")}
      >
        <Settings3Line size="xs" aria-hidden="true" />
      </span>
    );
  }

  return <Initial login={author.id} name={author.name ?? author.id} />;
}

// #region CSS
const setupCommentAvatar = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  font: ${fonts.body2};
  width: 1.6em;
  height: 1.6em;
  border-radius: 50%;
  background-color: ${setupModeColors.primary};
  color: ${setupModeColors.iconOnPrimary};
`;

const wrapper = css`
  display: flex;
  flex-direction: column-reverse;

  &.${OUTDATED_CN} {
    opacity: 0.5;
  }
`;

const titleWrapper = css`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  overflow: auto;
`;

const authorName = css`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const authorTag = css`
  flex-shrink: 0;
  padding: ${spacings.xxs};
  border-radius: 8px;
  text-align: center;
  background-color: ${color.lightNitrogen50};
  color: ${color.carbon900};
`;

const threadTagList = css`
  margin: ${spacings.xs} 0;
`;

const commentTag = css`
  &.${COMMENT_TAG_DELETED} {
    padding: 2px 0;
    color: ${colors.oxygen400};
    background-color: transparent;
  }
  &.${COMMENT_TAG_CARD_HOVERED} {
    color: ${colors.lightNitrogen500_Main};
    background-color: ${colors.darkNitrogen50};
  }
  &.${COMMENT_TAG_CARD_SELECTED} {
    color: ${colors.hydrogen};
    background-color: ${colors.darkNitrogen600};
  }
`;

// #endregion CSS
